import { createContext, type RefObject, Suspense, use, useRef } from 'react'
import { Outlet } from 'react-router'
import { AlbumCache, type AlbumCacheProps } from './AlbumCache'
import { SearchPhotosCache, type SearchPhotosCacheProps } from './SearchPhotosCache'
import type { Album, Share } from './types'
import { UnlimitedPartialPhotosCache, type UnlimitedCacheProps } from './UnlimitedPartialPhotosCache'

const UnlimitedPartialPhotosCacheContext = createContext<RefObject<UnlimitedPartialPhotosCache<unknown>>>(undefined as never)
export const PhotosCacheProvider = <T, >({
  listPhotos, listAllPhotosDate, removePhotos, getDirectory, getPhoto, tagsToPhoto, reactionToPhoto
}: UnlimitedCacheProps<T>) => {
  const photo_cache = useRef(new UnlimitedPartialPhotosCache<T>({
    listPhotos, listAllPhotosDate, removePhotos, getDirectory, getPhoto, tagsToPhoto, reactionToPhoto
  }))
  // use()でsuspendする子（Photo.tsx）がここより外のSuspense境界で捕まると、初回マウントの
  // リトライ毎に未コミットのuseRef（cacheインスタンス）が作り直され、getPhotoが毎回別Promiseを
  // 返して無限suspendする。Provider直下に境界を置き、cacheを先にコミットして安定させる。
  return <UnlimitedPartialPhotosCacheContext value={photo_cache}>
    <Suspense>
      <Outlet/>
    </Suspense>
  </UnlimitedPartialPhotosCacheContext>
}
export const usePartialPhotosCache = <T, >() => use(UnlimitedPartialPhotosCacheContext).current as UnlimitedPartialPhotosCache<T>

const SearchPhotosCacheContext = createContext<RefObject<SearchPhotosCache>>(undefined as never)
export const SearchPhotosCacheProvider = ({
  getUser, searchAllPhotosDateWithId, searchPhotos, removePhotos, getPhoto, tagsToPhoto, reactionToPhoto
}: SearchPhotosCacheProps) => {
  const photo_cache = useRef(new SearchPhotosCache({
    getUser, searchPhotos, searchAllPhotosDateWithId, removePhotos, getPhoto, tagsToPhoto, reactionToPhoto
  }))
  return <SearchPhotosCacheContext value={photo_cache}>
    <Suspense>
      <Outlet/>
    </Suspense>
  </SearchPhotosCacheContext>
}
export const useSearchPhotosCache = () => use(SearchPhotosCacheContext).current as SearchPhotosCache

const AlbumCacheContext = createContext<RefObject<AlbumCache<Album>>>(undefined as never)
export const AlbumCacheProvider = <T extends Album | Share>({
  getAlbum, removePhotos, getPhoto, tagsToPhoto, reactionToPhoto
}: AlbumCacheProps<T>) => {
  const album_cache = useRef(new AlbumCache({getAlbum, removePhotos, getPhoto, tagsToPhoto, reactionToPhoto}))
  // PhotosCacheProviderと同理由でcacheインスタンスを安定させるため、境界をProvider直下に置く。
  return <AlbumCacheContext value={album_cache}>
    <Suspense>
      <Outlet/>
    </Suspense>
  </AlbumCacheContext>
}
export const useAlbumCache = <T extends Album | Share>() => use(AlbumCacheContext).current as AlbumCache<T>
